// ABOUTME: Shared AppBar component used across all pages for consistent navigation
// ABOUTME: Implements white theme styling and common menu functionality

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Button,
  Link,
  Box,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  TrackChanges as GoalsIcon,
  Checklist as ChoresIcon,
  Analytics as AnalyticsIcon,
  Insights as InsightsIcon,
  People as FamilyIcon,
  EventNote as EventLogIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  SettingsApplications as IntegrationsIcon,
  Info as InfoIcon,
  MenuBook as KnowledgeBaseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../hooks/useImpersonation';
import { useFamilyTerminology } from '../hooks/useFamilyTerminology';
import { getAvatarUrl } from '../utils/avatarUtils';
import naluV1Theme from '../theme/theme.nalu-v1';

interface SharedAppBarProps {
  title: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  onMenuClick?: (action: string) => void;
  position?: 'static' | 'sticky' | 'fixed';
}

const SharedAppBar: React.FC<SharedAppBarProps> = ({
  title,
  showBackButton = false,
  onBackClick,
  onMenuClick,
  position = 'static'
}) => {
  const { user, logout } = useAuth();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const { Term, basePath } = useFamilyTerminology();
  const location = useLocation();
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Show public branding/navigation for unauthenticated visitors on KB routes
  const isPublicKbRoute = !user && (
    location.pathname === '/knowledge-base' ||
    location.pathname.startsWith('/knowledge-base/')
  );

  // Use impersonated user's profile data when impersonating, otherwise use admin's data
  const displayUser = isImpersonating && impersonatedUser ? {
    profileImageUrl: impersonatedUser.profileImageUrl,
    firstName: impersonatedUser.firstName,
    lastName: impersonatedUser.lastName,
    email: impersonatedUser.email,
  } : {
    profileImageUrl: user?.profileImageUrl,
    firstName: user?.firstName,
    lastName: user?.lastName,
    email: user?.email,
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: string) => {
    handleMenuClose();
    if (action === 'logout') {
      void logout();
    } else if (action === 'back' && onBackClick) {
      onBackClick();
    } else if (action === 'Dashboard') {
      window.location.href = '/';
    } else if (action.startsWith('/')) {
      // Handle route navigation
      window.location.href = action;
    } else if (onMenuClick) {
      onMenuClick(action);
    }
  };

  const getUserInitials = (email?: string): string => {
    if (!email) return '?';
    return email
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <AppBar
      // nalu-v1 always sticks the header — a deliberate site-wide look (matching every
      // page's header in the mockups), not just this page's own `position` prop (issue #1900)
      position={isNaluV1Theme ? 'sticky' : position}
      sx={isNaluV1Theme ? {
        bgcolor: alpha(theme.palette.background.default, 0.5),
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        color: 'text.primary',
        boxShadow: 'none',
        borderRadius: 0, // square ends, not the theme's default rounded Paper corners
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderBottom: '1px solid',
        borderBottomColor: alpha(theme.palette.warning.main, 0.28),
      } : {
        bgcolor: 'background.paper',
        color: 'text.primary',
        marginBottom: position === 'static' ? '4px' : '0',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: position === 'static' ? 1 : (theme) => `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
        borderBottom: position !== 'static' ? '1px solid' : 'none',
        borderBottomColor: position !== 'static' ? 'divider' : 'transparent'
      }}
    >
      <Toolbar>
        {showBackButton && (
          <IconButton
            edge="start"
            onClick={() => handleMenuItemClick('back')}
            sx={{ mr: 2, color: 'text.primary' }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box
          component="img"
          src={theme.logoSrc}
          alt="Let's Reset"
          sx={{ height: isNaluV1Theme ? 44 : 32, width: 'auto', mr: 1.5 }}
        />
        {isNaluV1Theme ? (
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontFamily: theme.typography.h6.fontFamily, fontSize: '22px', fontWeight: 600, lineHeight: 1, color: 'text.primary' }}>
              {isPublicKbRoute ? "Let's Reset" : title}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              Let&apos;s Reset
            </Typography>
          </Box>
        ) : (
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
            {isPublicKbRoute ? "Let's Reset" : title}
          </Typography>
        )}
        {isPublicKbRoute && (
          <>
            <Link
              href="/knowledge-base"
              underline="hover"
              sx={{ mr: 2, color: 'text.primary', fontWeight: 600 }}
            >
              Knowledge Base
            </Link>
            <Button
              variant="contained"
              size="small"
              href="/login"
            >
              Sign In
            </Button>
          </>
        )}
        {user && (
          <>
            <IconButton
              size="large"
              onClick={handleMenuOpen}
              sx={{ p: 0.5, color: 'text.primary' }}
            >
              <Avatar
                src={getAvatarUrl(displayUser.profileImageUrl)}
                alt={displayUser.firstName || displayUser.lastName ? `${displayUser.firstName || ''} ${displayUser.lastName || ''}`.trim() : displayUser.email || 'User'}
                sx={{ width: 40, height: 40 }}
              >
                {!displayUser.profileImageUrl && getUserInitials(displayUser.email)}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  borderRadius: 1, // theme.shape.borderRadius(12) * 1 = 12px
                  boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                  minWidth: 220,
                }
              }}
            >
              <MenuItem onClick={() => handleMenuItemClick('Dashboard')}>
                <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Dashboard</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/analytics')}>
                <ListItemIcon><AnalyticsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Analytics</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/analytics-v2')}>
                <ListItemIcon><InsightsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Family Insights (Preview)</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/event-log')}>
                <ListItemIcon><EventLogIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Event Log</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              <MenuItem onClick={() => handleMenuItemClick(basePath)}>
                <ListItemIcon><FamilyIcon fontSize="small" /></ListItemIcon>
                <ListItemText>My {Term}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/goals')}>
                <ListItemIcon><GoalsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Goals</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/responsibilities')}>
                <ListItemIcon><ChoresIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Responsibilities</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/integrations')}>
                <ListItemIcon><IntegrationsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Integrations</ListItemText>
              </MenuItem>

              {user?.isAdmin && (
                <MenuItem onClick={() => handleMenuItemClick('/knowledge-base')}>
                  <ListItemIcon><KnowledgeBaseIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Knowledge Base</ListItemText>
                </MenuItem>
              )}

              {user?.isAdmin && !isImpersonating && [
                <Divider key="admin-divider" sx={{ my: 1 }} />,
                <MenuItem key="admin-menu-item" onClick={() => handleMenuItemClick('/admin')}>
                  <ListItemIcon><AdminIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Admin</ListItemText>
                </MenuItem>
              ]}

              <Divider sx={{ my: 1 }} />

              <MenuItem onClick={() => handleMenuItemClick('/profile')}>
                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                <ListItemText>My Profile</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              <MenuItem onClick={() => handleMenuItemClick('/about')}>
                <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                <ListItemText>About</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              <MenuItem onClick={() => handleMenuItemClick('logout')}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default SharedAppBar;